import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState } from "react";
import OtpInputComponent from "./OtpInput";
import { useOtpVerification } from "./useOtpVerification";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    minWidth: 400,
    maxWidth: 500,
    [theme.breakpoints.down("sm")]: {
      minWidth: 320,
      margin: 16,
    },
  },
}));

const QRCodeContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2),
}));

const steps = ["Setup Authenticator", "Verify OTP"];

function MfaDialog({ open, onClose, qrUrl, tempToken, onSuccess }) {
  const [activeStep, setActiveStep] = useState(0);
  const {
    otp,
    setOtp,
    loading,
    error,
    setError,
    handleVerifyOtp,
    handleKeyPress,
    resetOtp,
  } = useOtpVerification(tempToken, onSuccess);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
    setError("");
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError("");
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setActiveStep(0);
      resetOtp();
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" gutterBottom>
              Set up Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Scan the QR code below with your authenticator app to enable 2FA
              for your account.
            </Typography>

            <QRCodeContainer>
              {qrUrl ? (
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrUrl)}&size=200x200&margin=10`}
                  alt="QR Code"
                  style={{
                    borderRadius: 8,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "white",
                    padding: 8,
                  }}
                />
              ) : (
                <CircularProgress size={60} />
              )}
            </QRCodeContainer>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Recommended apps:</strong> Google Authenticator,
                Microsoft Authenticator, or any TOTP-compatible app.
              </Typography>
            </Alert>

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                After scanning the QR code, click "Next" to proceed with
                verification.
              </Typography>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ textAlign: "center" }}>
            <OtpInputComponent
              otp={otp}
              setOtp={setOtp}
              error={error}
              setError={setError}
              onKeyPress={handleKeyPress}
              onVerify={handleVerifyOtp}
              label="6-digit verification code"
              infoText="The code refreshes every 30 seconds. Make sure to enter the current code from your authenticator app."
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          Two-Factor Authentication Setup
        </Typography>
        <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {renderStepContent(activeStep)}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} variant="outlined">
          Cancel
        </Button>

        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading} variant="outlined">
            Back
          </Button>
        )}

        {activeStep === 0 ? (
          <Button
            onClick={handleNext}
            disabled={!qrUrl || loading}
            variant="contained"
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleVerifyOtp}
            disabled={otp.length !== 6 || loading}
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? "Verifying..." : "Verify & Sign In"}
          </Button>
        )}
      </DialogActions>
    </StyledDialog>
  );
}

export default MfaDialog;
